(() => {
  'use strict';

  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const config = window.NuvexTTheme || {};
  const routes = config.routes || {};

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount}}', withCommas)
      .replace('{{amount_no_decimals}}', String(Math.round(Number(cents || 0) / 100)))
      .replace('{{amount_with_comma_separator}}', withCommas.replace(',', 'X').replace('.', ',').replace('X', '.'));
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = Boolean(hidden);
  }

  /* Mobile navigation */
  const mobileMenu = q('[data-mobile-menu]');
  const mobileToggle = q('[data-mobile-menu-toggle]');
  const closeMobileMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.hidden = true;
    document.body.classList.remove('menu-open');
    mobileToggle?.setAttribute('aria-expanded', 'false');
    mobileToggle?.focus({ preventScroll: true });
  };
  const openMobileMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.hidden = false;
    document.body.classList.add('menu-open');
    mobileToggle?.setAttribute('aria-expanded', 'true');
    q('.mobile-menu__panel .icon-button', mobileMenu)?.focus({ preventScroll: true });
  };
  mobileToggle?.addEventListener('click', openMobileMenu);
  qa('[data-mobile-menu-close]').forEach((button) => button.addEventListener('click', closeMobileMenu));

  /* Product galleries */
  qa('[data-product-gallery]').forEach((gallery) => {
    const main = q('[data-gallery-main]', gallery);
    const thumbs = qa('[data-gallery-thumb]', gallery);
    let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));

    const activate = (index) => {
      if (!main || !thumbs.length) return;
      activeIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[activeIndex];
      thumbs.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === activeIndex));
      main.classList.add('is-switching');
      const nextSrc = thumb.dataset.src;
      const nextSrcset = thumb.dataset.srcset;
      const nextSizes = thumb.dataset.sizes;

      if (nextSizes) main.setAttribute('sizes', nextSizes);
      else main.removeAttribute('sizes');

      if (nextSrcset) main.setAttribute('srcset', nextSrcset);
      else main.removeAttribute('srcset');

      if (nextSrc) main.src = nextSrc;
      if (thumb.dataset.width) main.setAttribute('width', thumb.dataset.width);
      if (thumb.dataset.height) main.setAttribute('height', thumb.dataset.height);
      main.alt = thumb.dataset.alt || '';

      const finishSwitch = () => main.classList.remove('is-switching');
      if (typeof main.decode === 'function') main.decode().then(finishSwitch).catch(finishSwitch);
      else window.setTimeout(finishSwitch, 170);
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));
    q('[data-gallery-prev]', gallery)?.addEventListener('click', () => activate(activeIndex - 1));
    q('[data-gallery-next]', gallery)?.addEventListener('click', () => activate(activeIndex + 1));

    let startX = 0;
    q('.product-gallery__main', gallery)?.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0]?.clientX || 0;
    }, { passive: true });
    q('.product-gallery__main', gallery)?.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - startX;
      if (Math.abs(distance) > 45) activate(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });
  });

  /* Quantity controls */
  qa('[data-quantity]').forEach((control) => {
    const input = q('[data-quantity-input]', control);
    q('[data-quantity-minus]', control)?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    q('[data-quantity-plus]', control)?.addEventListener('click', () => {
      input.value = Math.max(1, Number(input.value || 1) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* Variant updates */
  qa('[data-main-product]').forEach((section) => {
    const jsonNode = q('[data-product-json]', section);
    const variants = jsonNode ? JSON.parse(jsonNode.textContent || '[]') : [];
    const select = q('[data-variant-select]', section);
    const price = q('[data-product-price]', section);
    const compare = q('[data-product-compare]', section);
    const save = q('[data-product-save]', section);
    const stickyPrice = q('[data-sticky-price]', section);
    const submit = q('[data-add-to-cart]', section);
    const submitText = q('[data-add-to-cart-text]', section);

    const updateVariant = () => {
      const variant = variants.find((item) => String(item.id) === String(select?.value));
      if (!variant) return;
      if (price) price.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (compare) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.classList.remove('is-hidden');
        }
        if (save) {
          save.textContent = `SAVE ${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`;
          save.classList.remove('is-hidden');
        }
      } else {
        compare?.classList.add('is-hidden');
        save?.classList.add('is-hidden');
      }
      if (submit) submit.disabled = !variant.available;
      if (submitText) submitText.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      if (variant.featured_image?.src && q('.product-gallery--catalog', section)) {
        const imageId = String(variant.featured_image.id || '');
        const matchingThumb = qa('[data-gallery-thumb]', section).find((thumb) => thumb.dataset.imageId === imageId);
        if (matchingThumb) {
          matchingThumb.click();
        } else {
          const main = q('[data-gallery-main]', section);
          if (main) {
            main.removeAttribute('srcset');
            main.removeAttribute('sizes');
            main.src = variant.featured_image.src;
          }
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    select?.addEventListener('change', updateVariant);

    const purchase = q('[data-product-purchase]', section);
    const sticky = q('[data-sticky-add]', section);
    if (purchase && sticky && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !visible;
      }, { threshold: 0.04 });
      observer.observe(purchase);
    }
    q('[data-sticky-submit]', section)?.addEventListener('click', () => q('[data-product-form]', section)?.requestSubmit());
  });

  /* Cart drawer and Ajax cart */
  const drawer = q('[data-cart-drawer]');
  const drawerPanel = q('.cart-drawer__panel', drawer || document);
  const cartItems = q('[data-cart-items]', drawer || document);
  const cartFooter = q('[data-cart-footer]', drawer || document);
  const cartSubtotal = q('[data-cart-subtotal]', drawer || document);
  const cartShipping = q('[data-cart-shipping]', drawer || document);
  let lastFocused = null;

  async function getCart() {
    const response = await fetch(`${routes.cart}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function updateCartCount(count) {
    qa('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.closest('[data-cart-open]')?.setAttribute('aria-label', `Cart with ${count} items`);
    });
  }

  function curatedProductImage(item) {
    const key = `${item?.handle || ''} ${item?.product_title || ''} ${item?.url || ''}`.toLowerCase();
    const media = config.productMedia || {};
    if (key.includes('glove')) return media.gloves || item?.image || '';
    if (key.includes('potion')) return media.potion || item?.image || '';
    if (key.includes('dashboard') || key.includes('phone-holder') || key.includes('phone holder')) return media.dashboard || item?.image || '';
    return item?.image || '';
  }

  function renderCart(cart) {
    updateCartCount(cart.item_count || 0);
    if (!cartItems || !cartFooter) return;
    if (!cart.items?.length) {
      cartItems.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      cartFooter.hidden = true;
      return;
    }
    cartItems.innerHTML = cart.items.map((item) => `
      <article class="cart-drawer__item">
        <a href="${escapeHtml(item.url)}"><img src="${escapeHtml(curatedProductImage(item))}" width="82" height="82" loading="lazy" decoding="async" alt="${escapeHtml(item.product_title)}"></a>
        <div>
          <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</span>` : ''}
          <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
          <div class="cart-drawer__quantity" aria-label="Quantity for ${escapeHtml(item.product_title)}">
            <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="cart-drawer__remove" type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="0" aria-label="Remove ${escapeHtml(item.product_title)}">×</button>
      </article>`).join('');
    cartSubtotal.textContent = formatMoney(cart.total_price);
    const threshold = Number(config.freeShippingThreshold || 0);
    if (cartShipping && threshold > 0) {
      const remaining = Math.max(0, threshold - cart.total_price);
      const progress = Math.min(100, (cart.total_price / threshold) * 100);
      cartShipping.innerHTML = remaining > 0
        ? `<span>You are <strong>${formatMoney(remaining)}</strong> away from free shipping.</span><div class="cart-drawer__shipping-progress"><b style="width:${progress}%"></b></div>`
        : '<span><strong>You qualify for free shipping.</strong></span><div class="cart-drawer__shipping-progress"><b style="width:100%"></b></div>';
    }
    cartFooter.hidden = false;
  }

  async function refreshCart() {
    try {
      const cart = await getCart();
      renderCart(cart);
      return cart;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function openCart() {
    if (!drawer || !config.cartDrawer) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('cart-open');
    drawerPanel?.focus({ preventScroll: true });
    refreshCart();
  }

  function closeCart() {
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('cart-open');
    if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
  }

  qa('[data-cart-open]').forEach((link) => link.addEventListener('click', (event) => {
    if (!config.cartDrawer) return;
    event.preventDefault();
    openCart();
  }));
  qa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));

  drawer?.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-change]');
    if (!button) return;
    button.disabled = true;
    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: button.dataset.cartChange, quantity: Number(button.dataset.cartQuantity || 0) })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      renderCart(await response.json());
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  });

  qa('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!config.cartDrawer) return;
      event.preventDefault();
      const submit = q('[data-add-to-cart]', form);
      const text = q('[data-add-to-cart-text]', form);
      const status = q('[data-product-status]', form);
      const original = text?.textContent || 'ADD TO CART';
      if (submit) submit.disabled = true;
      if (text) text.textContent = 'ADDING…';
      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this item');
        if (status) status.textContent = 'Added to your cart.';
        await refreshCart();
        openCart();
      } catch (error) {
        if (status) status.textContent = error.message || 'Unable to add this item.';
      } finally {
        if (submit) submit.disabled = false;
        if (text) text.textContent = original;
      }
    });
  });

  refreshCart();

  /* Testimonial controls */
  qa('[data-testimonials]').forEach((section) => {
    const track = q('[data-testimonials-track]', section);
    if (!track) return;
    q('[data-testimonials-next]', section)?.addEventListener('click', () => {
      const first = q('[data-testimonial-card]', track);
      if (first) track.append(first);
    });
    q('[data-testimonials-prev]', section)?.addEventListener('click', () => {
      const cards = qa('[data-testimonial-card]', track);
      const last = cards.at(-1);
      if (last) track.prepend(last);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (drawer && !drawer.hidden) closeCart();
    if (mobileMenu && !mobileMenu.hidden) closeMobileMenu();
  });
})();
